import { useEffect, useState, type PointerEvent, type ReactElement } from 'react';
import type { MapGrid, SpotItem } from '@kratispot/shared';
import { scrollTargetFor, viewportWindow } from '@/lib/map-fit';
import { useTranslation } from '@/lib/i18n';
import { cn } from '@/lib/utils';

const MINI_TONE: Readonly<Record<string, string>> = {
  free: 'bg-emerald-200',
  reserved: 'bg-amber-500',
  occupied: 'bg-red-500',
  cleaning: 'bg-sky-500',
  past: 'bg-stone-300',
  history: 'bg-violet-300',
};

// What the venue is made of, in the same colours the floor draws them. §6
const ELEMENT_TONE: Readonly<Record<string, string>> = {
  deck: 'bg-amber-300',
  bar: 'bg-stone-700',
  kitchen: 'bg-stone-500',
  wall: 'bg-stone-400',
};

function toneOf(item: SpotItem): string {
  const { status, occupations } = item.spot;
  if (status !== null) return MINI_TONE[status] as string;
  return occupations > 0 ? (MINI_TONE.history as string) : (MINI_TONE.past as string);
}

// The whole venue as a live heat-strip, and the way to move the view. §15
export function Minimap({
  grid,
  scroller,
}: {
  grid: MapGrid;
  scroller: HTMLElement | null;
}): ReactElement {
  const { t } = useTranslation();
  const [view, setView] = useState({ leftPct: 0, widthPct: 100 });

  useEffect(() => {
    if (scroller === null) return;
    const sync = (): void =>
      setView(viewportWindow(scroller.scrollLeft, scroller.scrollWidth, scroller.clientWidth));
    sync();
    scroller.addEventListener('scroll', sync, { passive: true });
    return () => scroller.removeEventListener('scroll', sync);
  }, [scroller]);

  function jump(event: PointerEvent<HTMLDivElement>): void {
    if (scroller === null) return;
    const box = event.currentTarget.getBoundingClientRect();
    const fraction = box.width === 0 ? 0 : (event.clientX - box.left) / box.width;
    scroller.scrollLeft = scrollTargetFor(fraction, scroller.scrollWidth, scroller.clientWidth);
    scroller.dispatchEvent(new Event('scroll'));
  }

  return (
    <div className="border-b border-dashed border-amber-200 bg-amber-50/70 px-3 pt-2.5 pb-2">
      <div
        role="presentation"
        data-testid="minimap"
        className="relative cursor-pointer touch-none"
        onPointerDown={jump}
        onPointerMove={(event) => {
          if (event.buttons !== 0) jump(event);
        }}
      >
        {/* The rows are the grid's, not each tile's: a run of decking spans
            five of them and a bar spans a dozen columns, and either drawn one
            cell tall leaves a hole where the venue's own shape should be. */}
        <div
          className="grid gap-[2px]"
          style={{
            gridTemplateColumns: `repeat(${grid.cols}, minmax(0, 1fr))`,
            gridAutoRows: '9px',
          }}
        >
          {grid.items.map((item) => (
            <i
              key={`${item.gridRow}:${item.gridCol}`}
              className={cn(
                'block h-full rounded-[2px]',
                item.kind === 'spot' ? toneOf(item) : ELEMENT_TONE[item.element.kind],
              )}
              style={{
                gridRow: `${item.gridRow} / span ${item.rowSpan}`,
                gridColumn: `${item.gridCol} / span ${item.colSpan}`,
              }}
            />
          ))}
        </div>
        <span
          data-testid="minimap-view"
          aria-hidden
          className="pointer-events-none absolute -top-1 -bottom-1 rounded-md border-2 border-primary bg-primary/10"
          style={{ left: `${view.leftPct}%`, width: `${view.widthPct}%` }}
        />
      </div>
      <p className="pt-1.5 text-center text-[10.5px] font-semibold text-amber-700/80">
        {t('minimapHint')}
      </p>
    </div>
  );
}
